import React, { useContext } from 'react';
import { toast } from 'react-toastify';
import { userAPI } from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import styled from 'styled-components';
import { Card, ModernButton, LoadingSpinner, Badge } from './SharedComponents';
import { FiRefreshCw, FiUserPlus } from './Icons';

const Container = styled.div`
  padding: 0;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${(p) => p.theme.spacing.lg};
  flex-wrap: wrap;
  gap: ${(p) => p.theme.spacing.md};

  h2 {
    margin: 0;
    font-size: 1.15rem;
  }

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.sm};
  flex-wrap: wrap;

  @media (max-width: ${(p) => p.theme.breakpoints.mobile}) {
    justify-content: stretch;

    button {
      flex: 1;
    }
  }
`;

const TableContainer = styled(Card)`
  overflow-x: auto;
  padding: 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-family: ${(p) => p.theme.fonts.main};

  th, td {
    padding: 10px 14px;
    text-align: left;
    border-bottom: 1px solid ${(p) => p.theme.colors.border.light};
  }

  th {
    background: ${(p) => p.theme.colors.surfaceAlt};
    color: ${(p) => p.theme.colors.text.secondary};
    font-weight: 600;
    font-size: 0.72rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    position: sticky;
    top: 0;
    z-index: 1;
    white-space: nowrap;
  }

  td {
    font-size: 0.92rem;
    color: ${(p) => p.theme.colors.text.primary};
    vertical-align: middle;
  }

  tbody tr {
    transition: background 0.12s ease;

    &:hover {
      background: ${(p) => p.theme.colors.surfaceAlt};
    }

    &:last-child td {
      border-bottom: none;
    }

    &.inactive td {
      color: ${(p) => p.theme.colors.text.light};
    }
  }
`;

const ActionGroup = styled.div`
  display: flex;
  gap: ${(p) => p.theme.spacing.xs};
  flex-wrap: wrap;
`;

// Canonical bright role palette (matches Branch Management / calendars).
const RolePill = styled(Badge)`
  white-space: nowrap;

  &.admin { background: #930c0018; color: #930c00; border-color: #930c0033; }
  &.store_manager,
  &.manager_assistant { background: #e91e6318; color: #c2185b; border-color: #e91e6333; }
  &.makeup,
  &.makeup_supervisor { background: #9c27b018; color: #9c27b0; border-color: #9c27b033; }
  &.photographer,
  &.photographer_supervisor { background: #2196f318; color: #1976d2; border-color: #2196f333; }
  &.retoucher,
  &.retoucher_supervisor { background: #4caf5018; color: #2e7d32; border-color: #4caf5033; }
`;

const HolidayPill = styled(Badge)`
  white-space: nowrap;

  &.a { background: #3a6ea518; color: #3a6ea5; border-color: #3a6ea533; }
  &.b { background: #6b4a9e18; color: #6b4a9e; border-color: #6b4a9e33; }
`;

const EmptyState = styled(Card)`
  text-align: center;
  padding: ${(p) => p.theme.spacing.xxl};

  h3 {
    margin-bottom: ${(p) => p.theme.spacing.sm};
  }

  p {
    color: ${(p) => p.theme.colors.text.secondary};
    margin-bottom: ${(p) => p.theme.spacing.lg};
  }
`;

const LoadingContainer = styled(Card)`
  text-align: center;
  padding: ${(p) => p.theme.spacing.xxl};

  p {
    color: ${(p) => p.theme.colors.text.secondary};
    margin-top: ${(p) => p.theme.spacing.md};
  }
`;

const prettyRole = (role) => (role || '').replace(/_/g, ' ');

const EmployeeList = ({
  employees = [],
  loading = false,
  onEdit,
  onAdd,
  onRefresh,
}) => {
  const { user: currentUser } = useContext(AuthContext);

  const handleDelete = async (employee) => {
    if (!window.confirm(`Delete ${employee.name}? This cannot be undone.`)) return;
    try {
      await userAPI.delete(employee.id);
      toast.success(`${employee.name} deleted`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting employee:', error);
      toast.error(error.response?.data?.detail || `Failed to delete ${employee.name}`);
    }
  };

  const canManageUsers = currentUser && ['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor'].includes(currentUser.role);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <LoadingSpinner size="40px" />
          <p>Loading employees…</p>
        </LoadingContainer>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <h2>Branch employees ({employees.length})</h2>
        <ButtonGroup>
          {onRefresh && (
            <ModernButton onClick={onRefresh} variant="secondary" size="small">
              <FiRefreshCw size={14} /> Refresh
            </ModernButton>
          )}
          {canManageUsers && onAdd && (
            <ModernButton onClick={onAdd} size="small">
              <FiUserPlus size={14} /> Add employee
            </ModernButton>
          )}
        </ButtonGroup>
      </Header>

      {employees.length === 0 ? (
        <EmptyState>
          <h3>No employees found</h3>
          <p>No employees found in this branch.</p>
          {canManageUsers && (
            <ModernButton onClick={onAdd}><FiUserPlus size={14} /> Add first employee</ModernButton>
          )}
        </EmptyState>
      ) : (
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Holiday group</th>
                <th>Type</th>
                <th>Status</th>
                {canManageUsers && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {employees.map((employee) => (
                <tr key={employee.id} className={employee.is_active ? '' : 'inactive'}>
                  <td><strong>{employee.name}</strong></td>
                  <td>{employee.email}</td>
                  <td>
                    <RolePill className={employee.role}>{prettyRole(employee.role)}</RolePill>
                  </td>
                  <td>
                    {employee.holiday_group ? (
                      <HolidayPill className={String(employee.holiday_group.name).toLowerCase()}>
                        Group {employee.holiday_group.name}
                      </HolidayPill>
                    ) : (
                      <span style={{ color: '#9c948a' }}>—</span>
                    )}
                  </td>
                  <td>
                    {employee.is_moveable ? (
                      <Badge variant="info">Moveable</Badge>
                    ) : (
                      <Badge>Fixed</Badge>
                    )}
                  </td>
                  <td>
                    <Badge variant={employee.is_active ? 'success' : 'default'}>
                      {employee.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </td>
                  {canManageUsers && (
                    <td>
                      <ActionGroup>
                        <ModernButton
                          onClick={() => onEdit(employee)}
                          variant="outline"
                          size="small"
                        >
                          Edit
                        </ModernButton>
                        {employee.id !== currentUser.id && (
                          <ModernButton
                            onClick={() => handleDelete(employee)}
                            variant="secondary"
                            size="small"
                          >
                            Delete
                          </ModernButton>
                        )}
                      </ActionGroup>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default EmployeeList;
